import { collection, query, getDocs, orderBy, where, addDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { logger } from '../utils/logger';

export type RateType = 'fixed' | 'ic_plus';
export type RateStatus = 'none' | 'offered' | 'accepted' | 'declined' | 'revision_requested';
export type CardBrand = 'visa' | 'mastercard' | 'discover' | 'amex';

export interface CardRate {
  percent: number;
  fee: number;
}

export interface RateOffer {
  type: RateType;
  cards: Record<CardBrand, CardRate>;
  status?: RateStatus | 'superseded';
  offeredAt?: Date;
  respondedAt?: Date;
}

export interface AssignedRate {
  type: RateType;
  cards: Record<CardBrand, CardRate>;
  offeredAt?: Date;
  respondedAt?: Date;
}

const toDate = (value: any): Date | undefined => {
  if (!value) return undefined;
  if (value.toDate) return value.toDate();
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export const parseRateHistory = (history?: any[]): RateOffer[] => {
  if (!Array.isArray(history)) return [];
  return history.map((item) => ({
    type: item.type === 'ic_plus' ? 'ic_plus' : 'fixed',
    cards: item.cards,
    status: item.status,
    offeredAt: toDate(item.offeredAt),
    respondedAt: toDate(item.respondedAt),
  }));
};

export const getRateOffers = (quote: QuoteRequest): RateOffer[] => {
  const past = parseRateHistory(quote.rateHistory);
  if (!quote.rateCards) return past;
  return [
    ...past,
    {
      type: quote.rateType || 'fixed',
      cards: quote.rateCards,
      status: quote.rateStatus,
      offeredAt: quote.rateOfferedAt,
      respondedAt: quote.rateRespondedAt,
    },
  ];
};

export const CARD_BRANDS: { id: CardBrand; label: string }[] = [
  { id: 'visa', label: 'Visa' },
  { id: 'mastercard', label: 'Mastercard' },
  { id: 'discover', label: 'Discover' },
  { id: 'amex', label: 'Amex' },
];

export const emptyCardRates = (): Record<CardBrand, CardRate> => ({
  visa: { percent: 0, fee: 0 },
  mastercard: { percent: 0, fee: 0 },
  discover: { percent: 0, fee: 0 },
  amex: { percent: 0, fee: 0 },
});

export interface QuoteRequest {
  id: string;
  userId?: string;
  productId?: string;
  productName?: string;
  businessName: string;
  businessType: string;
  monthlyVolume: string;
  timeline: string;
  additionalNeeds?: string;
  status: 'pending' | 'in-progress' | 'completed' | 'declined' | 'denied' | 'cancelled';
  rateType?: RateType;
  rateCards?: Record<CardBrand, CardRate>;
  rateHistory?: RateOffer[];
  rateStatus?: RateStatus;
  rateOfferedAt?: Date;
  rateRespondedAt?: Date;
  createdAt: Date;
  customerEmail?: string;
  customerName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  businessAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  yearsInBusiness?: string;
  annualRevenue?: string;
  currentProcessor?: string;
  systemType?: string;
  message?: string;
}

// Get all quote requests
export const getQuoteRequests = async (): Promise<QuoteRequest[]> => {
  try {
    logger.log('🔍 Getting quote requests from Firestore...');
    
    const quoteRequestsCollection = collection(db, 'quoteRequests');
    const q = query(quoteRequestsCollection, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    logger.log('📊 Found quote requests:', querySnapshot.docs.length);
    
    const quoteRequests: QuoteRequest[] = [];
    
    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      logger.log('📋 Quote request:', {
        id: doc.id,
        businessName: data.businessName,
        productName: data.productName,
        status: data.status
      });
      
      quoteRequests.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
        rateOfferedAt: toDate(data.rateOfferedAt),
        rateRespondedAt: toDate(data.rateRespondedAt),
        rateHistory: parseRateHistory(data.rateHistory),
      } as QuoteRequest);
    }
    
    logger.log('✅ Returning quote requests list:', quoteRequests.length);
    return quoteRequests;
  } catch (error) {
    logger.error('💥 Error getting quote requests:', error);
    throw error;
  }
};

// Save a new quote request
export const saveQuoteRequest = async (quoteData: Omit<QuoteRequest, 'id'>): Promise<QuoteRequest> => {
  try {
    logger.log('💾 Saving quote request to Firestore...');
    logger.log('💾 Data being saved:', JSON.stringify(quoteData, null, 2));
    
    const quoteRequestsCollection = collection(db, 'quoteRequests');
    
    // Convert Date to Firestore Timestamp
    const dataToSave = {
      ...quoteData,
      createdAt: quoteData.createdAt || new Date()
    };
    
    logger.log('💾 Firestore collection path:', 'quoteRequests');
    logger.log('💾 Attempting to add document...');
    
    const docRef = await addDoc(quoteRequestsCollection, dataToSave);
    
    logger.log('✅ Quote request saved successfully!');
    logger.log('✅ Document ID:', docRef.id);
    logger.log('✅ Document path:', docRef.path);
    
    return {
      id: docRef.id,
      ...quoteData
    };
  } catch (error: any) {
    logger.error('💥 Error saving quote request to Firestore:');
    logger.error('💥 Error type:', error?.constructor?.name);
    logger.error('💥 Error message:', error?.message);
    logger.error('💥 Error code:', error?.code);
    logger.error('💥 Full error:', error);
    
    // Re-throw with more context
    const enhancedError = new Error(
      `Failed to save quote request: ${error?.message || 'Unknown error'} (Code: ${error?.code || 'UNKNOWN'})`
    );
    (enhancedError as any).originalError = error;
    (enhancedError as any).code = error?.code;
    throw enhancedError;
  }
};

// Get quote requests by user ID
export const getQuoteRequestsByUser = async (userId: string): Promise<QuoteRequest[]> => {
  try {
    const quoteRequestsCollection = collection(db, 'quoteRequests');
    const q = query(
      quoteRequestsCollection, 
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(docSnap => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
        rateOfferedAt: toDate(data.rateOfferedAt),
        rateRespondedAt: toDate(data.rateRespondedAt),
        rateHistory: parseRateHistory(data.rateHistory),
      } as QuoteRequest;
    });
  } catch (error) {
    logger.error('Error getting quote requests by user:', error);
    throw error;
  }
};

const applyRateUpdate = async (request: QuoteRequest, fields: Record<string, unknown>) => {
  await updateDoc(doc(db, 'quoteRequests', request.id), fields);
  if (!request.userId) return;
  const siblings = await getDocs(
    query(collection(db, 'quoteRequests'), where('userId', '==', request.userId))
  );
  await Promise.all(
    siblings.docs
      .filter((sibling) => sibling.id !== request.id)
      .map((sibling) => updateDoc(sibling.ref, fields))
  );
};

export const assignQuoteRate = async (
  request: QuoteRequest,
  rateType: RateType,
  rateCards: Record<CardBrand, CardRate>
) => {
  const history = [...(request.rateHistory || [])];
  if (request.rateCards) {
    const archived: RateOffer = {
      type: request.rateType || 'fixed',
      cards: request.rateCards,
      status: request.rateStatus === 'offered' ? 'superseded' : request.rateStatus || 'superseded',
      offeredAt: request.rateOfferedAt || new Date(),
    };
    if (request.rateRespondedAt) archived.respondedAt = request.rateRespondedAt;
    history.push(archived);
  }

  await applyRateUpdate(request, {
    rateType,
    rateCards,
    rateHistory: history,
    rateStatus: 'offered',
    status: 'in-progress',
    rateOfferedAt: new Date(),
    rateRespondedAt: null,
  });
};

export const respondToQuoteRate = async (
  requestId: string,
  accepted: boolean
) => {
  const requestRef = doc(db, 'quoteRequests', requestId);
  const requestSnap = await getDoc(requestRef);
  const request = { id: requestSnap.id, ...requestSnap.data() } as QuoteRequest;
  await applyRateUpdate(request, {
    rateStatus: accepted ? 'accepted' : 'declined',
    status: accepted ? 'completed' : 'declined',
    rateRespondedAt: new Date(),
  });
};

export const requestAnotherRate = async (requestId: string) => {
  const requestRef = doc(db, 'quoteRequests', requestId);
  const requestSnap = await getDoc(requestRef);
  const request = { id: requestSnap.id, ...requestSnap.data() } as QuoteRequest;
  await applyRateUpdate(request, {
    rateStatus: 'revision_requested',
    status: 'pending',
    rateRespondedAt: new Date(),
  });
};
